import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Slider,
  TextField,
  Box,
  FormHelperText,
} from '@mui/material';

export default function SettingsGroup({ title, description, settings, values, onChange }) {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>

        <Stack spacing={3}>
          {settings.map((setting) => {
            const currentValue = values?.[setting.key];

            if (setting.type === 'select') {
              return (
                <FormControl key={setting.key} fullWidth disabled={setting.disabled}>
                  <InputLabel>{setting.label}</InputLabel>
                  <Select
                    value={currentValue ?? ''}
                    label={setting.label}
                    onChange={(e) => onChange(setting.key, e.target.value)}
                  >
                    {setting.options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {setting.helperText && (
                    <FormHelperText>{setting.helperText}</FormHelperText>
                  )}
                </FormControl>
              );
            }

            if (setting.type === 'switch') {
              return (
                <Box key={setting.key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(currentValue)}
                        onChange={(e) => onChange(setting.key, e.target.checked)}
                        disabled={setting.disabled}
                      />
                    }
                    label={setting.label}
                  />
                  {setting.helperText && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                      {setting.helperText}
                    </Typography>
                  )}
                </Box>
              );
            }

            if (setting.type === 'slider') {
              return (
                <Box key={setting.key}>
                  <Typography variant="body2" gutterBottom>
                    {setting.label}: {currentValue ?? 0}%
                  </Typography>
                  <Slider
                    value={currentValue ?? 0}
                    min={setting.min ?? 0}
                    max={setting.max ?? 100}
                    step={setting.step ?? 10}
                    onChange={(_, val) => onChange(setting.key, val)}
                    disabled={setting.disabled}
                    valueLabelDisplay="auto"
                  />
                </Box>
              );
            }

            if (setting.type === 'text') {
              return (
                <TextField
                  key={setting.key}
                  fullWidth
                  label={setting.label}
                  value={currentValue ?? ''}
                  onChange={(e) => onChange(setting.key, e.target.value)}
                  disabled={setting.disabled}
                  helperText={setting.helperText}
                />
              );
            }

            return null;
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

SettingsGroup.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  settings: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'switch', 'slider', 'text']).isRequired,
    })
  ).isRequired,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
